import type {
  CalendarFestival,
  CalendarTodayResponse,
  CalendarMonthResponse,
  CalendarUpcomingResponse,
  CalendarFestivalDetail,
  CalendarFestivalTimes,
  CalendarFestivalCityTimes,
  CalendarFestivalTimeWindow,
  CalendarFestivalPlaceTimes,
  CalendarPradoshPlaceTimes,
  CalendarEkadashiResponse,
  CalendarMasikShivratriResponse,
  CalendarPurnimaResponse,
  CalendarAmavasyaResponse,
  CalendarPradoshResponse,
  CalendarSankashtiResponse,
  CalendarSankrantiResponse,
  CalendarVratYearResponse,
  CalendarEkadashiPaaranPlace,
} from "@ishvaram/api-client";
import { apiRequest } from "./_fetch";

export type {
  CalendarFestival,
  CalendarTodayResponse,
  CalendarMonthResponse,
  CalendarUpcomingResponse,
  CalendarFestivalDetail,
  CalendarFestivalTimes,
  CalendarFestivalCityTimes,
  CalendarFestivalTimeWindow,
  CalendarFestivalPlaceTimes,
  CalendarPradoshPlaceTimes,
  CalendarEkadashiResponse,
  CalendarMasikShivratriResponse,
  CalendarPurnimaResponse,
  CalendarAmavasyaResponse,
  CalendarPradoshResponse,
  CalendarSankashtiResponse,
  CalendarSankrantiResponse,
  CalendarVratYearResponse,
  CalendarEkadashiPaaranPlace,
};

// The Ekadashi list element carries the full FestivalSummary shape from the
// backend (incl. `month_hi`), which the hand-maintained `CalendarFestival`
// override omits. Derive it from the response so consumers get the real fields.
export type CalendarEkadashiItem = CalendarEkadashiResponse["ekadashis"][number];
// Purnima / Amavasya / Pradosh list elements share the same FestivalSummary shape.
export type CalendarPurnimaItem = CalendarPurnimaResponse["purnimas"][number];
export type CalendarAmavasyaItem = CalendarAmavasyaResponse["amavasyas"][number];
export type CalendarPradoshItem = CalendarPradoshResponse["pradoshas"][number];

export type CalendarSankashtiItem = CalendarSankashtiResponse["sankashtis"][number];
export type CalendarMasikShivratriItem = CalendarMasikShivratriResponse["masik_shivratris"][number];
export type CalendarSankrantiItem = CalendarSankrantiResponse["sankrantis"][number];

export async function getCalendarToday(
  date?: string,
  /** Request timeout; the default is fetchWithTimeout's. */
  timeoutMs?: number
): Promise<CalendarTodayResponse> {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  const qs = params.toString();
  return apiRequest<CalendarTodayResponse>(
    `/calendar/today${qs ? `?${qs}` : ""}`,
    undefined,
    timeoutMs
  );
}

export async function getCalendarMonth(
  year: number,
  month: number,
  /** Request timeout; the default is fetchWithTimeout's. */
  timeoutMs?: number
): Promise<CalendarMonthResponse> {
  return apiRequest<CalendarMonthResponse>(
    `/calendar/month?year=${year}&month=${month}`,
    undefined,
    timeoutMs
  );
}

export async function getCalendarUpcoming(
  days = 30,
  date?: string,
  /** Request timeout; the default is fetchWithTimeout's. */
  timeoutMs?: number
): Promise<CalendarUpcomingResponse> {
  const params = new URLSearchParams({ days: String(days) });
  if (date) params.set("date", date);
  return apiRequest<CalendarUpcomingResponse>(
    `/calendar/upcoming?${params.toString()}`,
    undefined,
    timeoutMs
  );
}

export async function getCalendarEkadashi(year = 2026): Promise<CalendarEkadashiResponse> {
  return apiRequest<CalendarEkadashiResponse>(`/calendar/ekadashi?year=${year}`);
}

/**
 * The Ekadashi paaran (fast-breaking) window after one of /calendar/ekadashi's
 * dates at one place in India (IST), on the day after it: the window, its kind,
 * and the sunrise, Hari Vasara and Dwadashi times it is reckoned from. A date
 * that is not an Ekadashi date is a 404; a place outside India is a 400.
 */
export async function getCalendarEkadashiPaaranPlace(
  date: string,
  latitude: number,
  longitude: number
): Promise<CalendarEkadashiPaaranPlace> {
  const params = new URLSearchParams({
    date,
    latitude: String(latitude),
    longitude: String(longitude),
  });
  return apiRequest<CalendarEkadashiPaaranPlace>(`/calendar/ekadashi/paaran/place?${params}`);
}

export async function getCalendarPurnima(year = 2026): Promise<CalendarPurnimaResponse> {
  return apiRequest<CalendarPurnimaResponse>(`/calendar/purnima?year=${year}`);
}

export async function getCalendarAmavasya(year = 2026): Promise<CalendarAmavasyaResponse> {
  return apiRequest<CalendarAmavasyaResponse>(`/calendar/amavasya?year=${year}`);
}

export async function getCalendarPradosh(year = 2026): Promise<CalendarPradoshResponse> {
  return apiRequest<CalendarPradoshResponse>(`/calendar/pradosh?year=${year}`);
}

export async function getCalendarSankashti(year = 2026): Promise<CalendarSankashtiResponse> {
  return apiRequest<CalendarSankashtiResponse>(`/calendar/sankashti?year=${year}`);
}

export async function getCalendarMasikShivratri(
  year = 2026
): Promise<CalendarMasikShivratriResponse> {
  return apiRequest<CalendarMasikShivratriResponse>(`/calendar/masik-shivratri?year=${year}`);
}

export async function getCalendarSankranti(year = 2026): Promise<CalendarSankrantiResponse> {
  return apiRequest<CalendarSankrantiResponse>(`/calendar/sankranti?year=${year}`);
}

export async function getCalendarVratYear(
  year = 2026,
  /** Request timeout; the default is fetchWithTimeout's. */
  timeoutMs?: number
): Promise<CalendarVratYearResponse> {
  return apiRequest<CalendarVratYearResponse>(
    `/calendar/vrat-year?year=${year}`,
    undefined,
    timeoutMs
  );
}

/** `timeoutMs` aborts sooner than the 12 s default, for a render that goes on without it. */
export async function getCalendarFestival(
  id: string,
  timeoutMs?: number
): Promise<CalendarFestivalDetail> {
  return apiRequest<CalendarFestivalDetail>(`/calendar/festival/${id}`, undefined, timeoutMs);
}

/**
 * Engine-computed clock times for a festival's date in `year`, per city.
 * `timeoutMs` aborts the request sooner than the 12 s default, for a caller
 * whose render can go on without the answer.
 */
export async function getCalendarFestivalTimes(
  id: string,
  year: number,
  timeoutMs?: number
): Promise<CalendarFestivalTimes> {
  return apiRequest<CalendarFestivalTimes>(
    `/calendar/festival/${id}/times?year=${year}`,
    undefined,
    timeoutMs
  );
}

/**
 * The same clock times at one place in India: the table's per-city rules at the
 * place's coordinates (IST). A place within 5 km of a table city is answered at
 * that city's coordinates and names it (`table_city`), so the two agree.
 */
export async function getCalendarFestivalPlaceTimes(
  id: string,
  year: number,
  latitude: number,
  longitude: number
): Promise<CalendarFestivalPlaceTimes> {
  const params = new URLSearchParams({
    year: String(year),
    latitude: String(latitude),
    longitude: String(longitude),
  });
  return apiRequest<CalendarFestivalPlaceTimes>(`/calendar/festival/${id}/times/place?${params}`);
}

/**
 * Pradosh Kaal on one of /calendar/pradosh's dates at one place in India (IST):
 * sunset to one fifth of the night there, with that paksha's Trayodashi span and
 * whether it runs inside the window. A date that is not a Pradosh date is a 404;
 * a place outside India is a 400.
 */
export async function getCalendarPradoshPlaceTimes(
  date: string,
  latitude: number,
  longitude: number
): Promise<CalendarPradoshPlaceTimes> {
  const params = new URLSearchParams({
    date,
    latitude: String(latitude),
    longitude: String(longitude),
  });
  return apiRequest<CalendarPradoshPlaceTimes>(`/calendar/pradosh/times/place?${params}`);
}
